import notification from 'antd/lib/notification';
import { useEffect, useRef, useState } from 'react';

import useAutoCredRequestId from '@/components/App/NewOrder/hooks/useAutoCredRequestId';
import { handleError } from '@/services/handleError';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  getAttachedFilesByReqId,
  setShouldUpdateBankInfo
} from '@/store/financingStep.slice';
import { getForteStatus } from '@/store/offerStep.slice';

const useForteStatus = () => {
  const dispatch = useAppDispatch();

  const { selectedBank, shouldUpdateBankInfo } = useAppSelector(
    (state) => state.financingStep
  );

  const { requestId } = useAutoCredRequestId();

  const timeout = useRef<NodeJS.Timeout | null>(null);

  const [updateStatusLoading, setUpdateStatusLoading] = useState(false);
  const [forteBankStatus, setForteBankStatus] = useState('');
  const [pollingActive, setPollingActive] = useState(false);

  const handleUpdateForteStatus = async () => {
    try {
      setUpdateStatusLoading(true);
      const response = await dispatch(
        getForteStatus({ extUuid: selectedBank?.extId || '' })
      );
      let statusResponse: string | undefined;
      if (getForteStatus.fulfilled.match(response)) {
        statusResponse = response.payload.find(
          (response: { bank: { name: string } }) =>
            response.bank.name === 'forte'
        )?.status?.code;
      }
      setForteBankStatus(statusResponse || '');

      if (statusResponse === 'waiting_bank_response') {
        setPollingActive(true);
      }

      timeout.current = setTimeout(async () => {
        await dispatch(getAttachedFilesByReqId(requestId));
        notification.success({
          title: 'Данные обновлены',
          description: 'Данные были обновлены',
          duration: 5
        });
        setUpdateStatusLoading(false);
      }, 10000);
    } catch (error) {
      handleError(error, {
        customTitle: 'Ошибка обновления файлов',
        onError: () => {
          setUpdateStatusLoading(false);
        }
      });
    }
  };

  useEffect(() => {
    if (pollingActive) {
      const pollingInterval = 10000; // 10 секунд
      const pollingDuration = 120000; // 2 минуты
      let elapsed = 0;

      const interval = setInterval(async () => {
        elapsed += pollingInterval;
        if (elapsed >= pollingDuration) {
          clearInterval(interval);
          setPollingActive(false);
          notification.info({
            title: 'Переопрос статуса завершен',
            description: 'Переопрос статуса от банка был завершен.',
            duration: 5
          });
          return;
        }

        const response = await dispatch(
          getForteStatus({ extUuid: selectedBank?.extId || '' })
        );
        let statusResponse: string | undefined;
        if (getForteStatus.fulfilled.match(response)) {
          statusResponse = response.payload.find(
            (response: { bank: { name: string } }) =>
              response.bank.name === 'forte'
          )?.status?.code;
        }
        setForteBankStatus(statusResponse || '');

        if (statusResponse !== 'waiting_bank_response') {
          clearInterval(interval);
          setPollingActive(false);
          notification.success({
            title: 'Статус обновлен',
            description: 'Статус был успешно обновлен.',
            duration: 5
          });
        }
      }, pollingInterval);

      return () => clearInterval(interval);
    }
  }, [pollingActive]);

  useEffect(() => {
    if (forteBankStatus === 'waiting_bank_response') {
      setPollingActive(true);
    }
  }, [forteBankStatus]);

  useEffect(() => {
    if (shouldUpdateBankInfo === 'forte') {
      dispatch(setShouldUpdateBankInfo(''));
      handleUpdateForteStatus();

      return () => {
        if (timeout.current) clearTimeout(timeout.current);
      };
    }
  }, [shouldUpdateBankInfo]);

  useEffect(() => {
    if (!selectedBank || selectedBank.status.code !== 'waiting_of_financing')
      return;
    // Запрашиваем статус, как только попадаем на третий шаг
    handleUpdateForteStatus();

    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, []);

  return { updateStatusLoading, handleUpdateForteStatus };
};

export default useForteStatus;
